/** Hora de la tienda (misma zona que el backend / CALENDAR_TIMEZONE). */
export const PIZAS_STORE_TZ = 'America/Mexico_City';

/** México sin horario de verano desde 2022: UTC-6 fijo. */
const MEXICO_UTC_OFFSET_HOURS = 6;

function partsInMexico(ms) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: PIZAS_STORE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
}

function partValue(parts, type) {
  return parts.find((p) => p.type === type)?.value || '';
}

/** ISO UTC → valor para `<input type="datetime-local">` en hora de México. */
export function isoToDatetimeLocalValue(iso) {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const parts = partsInMexico(ms);
  const y = partValue(parts, 'year');
  const m = partValue(parts, 'month');
  const d = partValue(parts, 'day');
  const h = partValue(parts, 'hour');
  const mi = partValue(parts, 'minute');
  return `${y}-${m}-${d}T${h}:${mi}`;
}

/** datetime-local (hora México) → ISO UTC para guardar en API. */
export function datetimeLocalValueToIso(localValue) {
  const raw = String(localValue || '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(raw);
  if (!m) return raw;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  return new Date(Date.UTC(y, mo - 1, d, h + MEXICO_UTC_OFFSET_HOURS, mi, 0)).toISOString();
}

/** Marca de tiempo actual interpretada en hora de la tienda (ms UTC). */
export function nowMexicoMs() {
  const parts = partsInMexico(Date.now());
  const y = Number(partValue(parts, 'year'));
  const mo = Number(partValue(parts, 'month'));
  const d = Number(partValue(parts, 'day'));
  const h = Number(partValue(parts, 'hour'));
  const mi = Number(partValue(parts, 'minute'));
  return Date.UTC(y, mo - 1, d, h + MEXICO_UTC_OFFSET_HOURS, mi, 0);
}

/** Suma o resta minutos a un ISO; devuelve ISO UTC. */
export function addMinutesToIso(iso, deltaMinutes) {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms + deltaMinutes * 60000).toISOString();
}

/** YYYY-MM-DD en hora de la tienda (hoy). */
export function todayMexicoYmd() {
  return msToMexicoYmd(Date.now());
}

/** YYYY-MM-DD en hora de la tienda para un instante UTC. */
export function msToMexicoYmd(ms) {
  const parts = partsInMexico(ms);
  return `${partValue(parts, 'year')}-${partValue(parts, 'month')}-${partValue(parts, 'day')}`;
}

/** Inicio del día (ms UTC) para un YYYY-MM-DD en México. */
export function mexicoYmdStartMs(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').trim());
  if (!m) return NaN;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  return Date.UTC(y, mo - 1, d, MEXICO_UTC_OFFSET_HOURS, 0, 0);
}

/** 0 = lunes … 6 = domingo (zona tienda). */
export function mexicoWeekdayIndex(ymd) {
  const ms = mexicoYmdStartMs(ymd);
  if (Number.isNaN(ms)) return 0;
  const short = new Intl.DateTimeFormat('en-US', {
    timeZone: PIZAS_STORE_TZ,
    weekday: 'short',
  }).format(new Date(ms));
  const map = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  return map[short] ?? 0;
}

const WEEKDAY_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

/** Los 7 días de la semana (lun–dom) que contiene `anchorYmd`. */
export function weekDaysMexico(anchorYmd, selectedYmd, todayYmd = todayMexicoYmd()) {
  const anchorMs = mexicoYmdStartMs(anchorYmd);
  if (Number.isNaN(anchorMs)) return [];
  const mondayMs = anchorMs - mexicoWeekdayIndex(anchorYmd) * 86400000;
  return WEEKDAY_SHORT.map((label, i) => {
    const ms = mondayMs + i * 86400000;
    const ymd = msToMexicoYmd(ms);
    const dayNum = Number(partValue(partsInMexico(ms), 'day'));
    return {
      ymd,
      label,
      dayNum,
      isToday: ymd === todayYmd,
      isSelected: ymd === selectedYmd,
    };
  });
}

/** Fecha larga para encabezado (es-MX). */
export function formatMexicoDateLong(ymd) {
  const ms = mexicoYmdStartMs(ymd);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleDateString('es-MX', {
    timeZone: PIZAS_STORE_TZ,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Hora corta para tablas (es-MX, zona tienda). */
export function formatTimeMexico(iso) {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleTimeString('es-MX', {
    timeZone: PIZAS_STORE_TZ,
    hour: '2-digit',
    minute: '2-digit',
  });
}
